import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { createSafeContext } from '../context/createSafeContext';

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  // `navigator.platform` is deprecated but still the most reliable signal for
  // "which modifier does this keyboard call Cmd"; the UA-Data alternative is
  // async and not universally available.
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

/**
 * The Settings accelerator, as the individual KEYS to press.
 *
 * A list rather than one string, because the menu draws each key as its own
 * cap. Glued together as "Ctrl+," the accelerator read as a typo — a trailing
 * comma after a word, with nothing to say the comma IS the key rather than
 * punctuation ending the line. Every app that shows this hint separates them
 * for the same reason.
 *
 * Matches the platform convention users already have muscle memory for:
 * Cmd+, on Apple platforms, Ctrl+, everywhere else.
 */
export function settingsShortcutKeys(): readonly string[] {
  return [isApplePlatform() ? '\u2318' : 'Ctrl', ','];
}

/**
 * Whether the settings dialog is open.
 *
 * Deliberately NOT a `TerminalTab`: settings is a modal that overlays whatever
 * you were doing, so routing it as a tab would replace the chat pane and lose
 * the tab you came from — closing would have to guess where to send you back.
 * A boolean beside the tab state keeps "where I am" and "settings is open"
 * independent, which is what every comparable app does.
 *
 * Lives in a context rather than the layout's local state because the trigger
 * (the sidebar account menu) and the dialog itself sit in different subtrees.
 */
/**
 * Sections of the settings dialog. Ordered as declared in `SECTIONS`; this type
 * is the addressable name, so a caller can deep-link to one.
 */
export type SettingsSection =
  | 'personalization'
  | 'appearance'
  | 'model'
  | 'privacy'
  | 'account';

export interface SettingsState {
  readonly settingsOpen: boolean;
  /**
   * The most recent deep-link, or null to let the dialog choose its default.
   * A fresh object per request rather than a bare section name, so that a
   * REPEAT of the same section is still a new request the dialog can see:
   * with the dialog open on another pane, asking for a section the user had
   * deep-linked to earlier must navigate again, and a string that never
   * changed identity could not say so.
   */
  readonly sectionRequest: { readonly section: SettingsSection } | null;
  /**
   * Where the dialog should put focus back on close. `null` ⇒ fall back to the
   * dialog's own capture of what had focus when it mounted.
   */
  readonly returnFocusTo: HTMLElement | null;
}

export interface SettingsActions {
  /**
   * Open the dialog.
   *
   * `section` deep-links to one group, so a control elsewhere in the app can
   * send the user to the setting it is about instead of to a generic landing
   * pane. An options object rather than positional arguments because this is
   * the seam every future entry point will use.
   *
   * `returnFocusTo` is where focus should land on close — pass the durable
   * control the user came from, not the thing they clicked. This exists because
   * the obvious approach does not work: the dialog captures
   * `document.activeElement` on mount, but the account MENU ITEM that opened it
   * is unmounted the moment the menu closes, so restoring focus to it is a
   * no-op and the user is dumped on `<body>` — where the next Tab starts over
   * at the top of the page. The account BUTTON survives, so it is the correct
   * target.
   */
  readonly openSettings: (options?: {
    readonly section?: SettingsSection;
    readonly returnFocusTo?: HTMLElement | null;
  }) => void;
  readonly closeSettings: () => void;
}

export const [useSettingsState, SettingsStateProvider] =
  createSafeContext<SettingsState>('SettingsState');
export const [useSettingsActions, SettingsActionsProvider] =
  createSafeContext<SettingsActions>('SettingsActions');

export function SettingsProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [returnFocusTo, setReturnFocusTo] = useState<HTMLElement | null>(null);
  const [sectionRequest, setSectionRequest] = useState<{
    readonly section: SettingsSection;
  } | null>(null);

  // Read by `openSettings` and the keydown handler below, both of which keep
  // a stable identity — a ref rather than a dep so neither is re-created (and
  // the listener isn't torn down and re-bound) on every toggle.
  const settingsOpenRef = useRef(settingsOpen);
  settingsOpenRef.current = settingsOpen;

  const openSettings = useCallback(
    (options?: {
      readonly section?: SettingsSection;
      readonly returnFocusTo?: HTMLElement | null;
    }): void => {
      // Already open: NAVIGATE, don't reopen. Re-running the full open would
      // reset `returnFocusTo` mid-dialog, which re-fires the dialog's focus
      // effect — its cleanup momentarily hands focus to the element BEHIND
      // the modal before the re-run pulls it back (the same transient the
      // keydown handler below guards against). A requested section still
      // lands: the dialog follows `sectionRequest` changes while mounted.
      if (settingsOpenRef.current) {
        if (options?.section != null) {
          setSectionRequest({ section: options.section });
        }
        return;
      }
      setReturnFocusTo(options?.returnFocusTo ?? null);
      setSectionRequest(
        options?.section != null ? { section: options.section } : null,
      );
      setSettingsOpen(true);
    },
    [],
  );
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  // Cmd/Ctrl+, — the platform convention for preferences, and the shortcut the
  // account menu advertises.
  //
  // Bound on `document` because the terminal has no single focusable root that
  // reliably holds focus. Two guards keep that from being rude to a host page:
  // an already-handled event is left alone, and a text-entry target OUTSIDE the
  // terminal is skipped so a host's editor keeps its own bindings. A host that
  // needs the chord for itself can still win by calling `preventDefault` first.
  //
  // The text-entry guard is scoped to the host deliberately. The terminal's own
  // composer is a `<textarea>`, and it holds focus for most of the app's life —
  // an unscoped guard would kill the shortcut exactly when the account menu is
  // advertising it, and `Cmd`/`Ctrl+,` has no text-editing meaning to protect
  // inside our own fields.
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent): void {
      if (e.defaultPrevented) return;
      if (e.key !== ',') return;
      if (!(isApplePlatform() ? e.metaKey : e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      const isTextEntry =
        target?.isContentEditable === true ||
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA';
      // Ancestry, not a ref: this provider wraps the tree but renders no
      // element of its own. Portaled surfaces (the dialog, the sidebar menus)
      // sit outside the root and so still take the guard — which is what we
      // want, since a shortcut whose only job is to OPEN settings has nothing
      // to do while they are already open.
      if (isTextEntry && target?.closest('[data-terminal-root]') == null) {
        return;
      }
      e.preventDefault();
      // Already open: the shortcut's only job is to OPEN settings, so a
      // repeat press is a no-op. Re-running the open would reset
      // `returnFocusTo` mid-dialog, which re-fires the dialog's focus effect
      // — its cleanup momentarily hands focus to the element BEHIND the
      // modal before the re-run pulls it back.
      if (settingsOpenRef.current) return;
      // Opened by keyboard: whatever had focus is still mounted, so the
      // dialog's own capture is the right thing to restore, and no section was
      // requested.
      setReturnFocusTo(null);
      setSectionRequest(null);
      setSettingsOpen(true);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const state = useMemo<SettingsState>(
    () => ({ settingsOpen, returnFocusTo, sectionRequest }),
    [settingsOpen, returnFocusTo, sectionRequest],
  );
  const actions = useMemo<SettingsActions>(
    () => ({ openSettings, closeSettings }),
    [openSettings, closeSettings],
  );

  return (
    <SettingsStateProvider value={state}>
      <SettingsActionsProvider value={actions}>
        {children}
      </SettingsActionsProvider>
    </SettingsStateProvider>
  );
}
